import {
  CurrentlySelectedCollection,
  MediaUploadSettings,
  MediaCategory,
  MediaCollection,
  MediaProcessingType,
} from './../media-library.model';
import { Component, Inject, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { forkJoin, map, Observable, startWith, Subscription, timer } from 'rxjs';
import { WidgetService } from '../../services/widget.service';
import { AccountFileResponse } from 'app/campaign/widgets.model';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';

export interface UploadModalData {
  mediaUrl: string;
  collections: { [x: string]: Array<MediaCollection> };
  currentSelection?: CurrentlySelectedCollection;
  supportsVideos: boolean;
  file: File;
  fileName: string;
  isVideo: boolean;
}

@Component({
  selector: 'app-upload-modal',
  templateUrl: './upload-modal.component.html',
  styleUrl: './upload-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UploadModalComponent {
  mediaUrl: string;
  category: MediaCategory = MediaCategory.Photos;
  MediaCategory = MediaCategory;
  collection?: string = null;
  collections: Array<string> = [];
  collectionControl = new FormControl('');
  filteredOptions: Observable<string[]>;
  subscriptions: Subscription[] = [];
  supportsVideos: boolean;
  isUploadingVideo: boolean = false;
  isUploading: boolean = false;

  private widgetService = inject(WidgetService);
  private dialog = inject(MatDialog);

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: UploadModalData,
    private dialogRef: MatDialogRef<UploadModalComponent>,
  ) {}

  ngOnInit() {
    this.mediaUrl = this.data.mediaUrl;
    this.category = this.data.currentSelection.category;
    this.isUploadingVideo = this.category === MediaCategory.Videos;
    this.supportsVideos = this.data.supportsVideos;
    this.filteredOptions = this.collectionControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );
    this.onCategoryChange(null);
  }

  public addMedia() {
    let collection: string = null;
    if (this.collection) {
      collection = this.collection.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
    }

    const file = this.data.file;
    const isVideo = this.data.isVideo;
    const needsSizeConfirmation =
      file.size / 1024 > 500 && !file.type.includes('gif') && !file.type.includes('svg') && !isVideo;

    if (needsSizeConfirmation) {
      this.openSizeConfirmation(collection);
    } else {
      this.startUpload(collection, isVideo ? MediaProcessingType.Video : MediaProcessingType.Original);
    }
  }

  private openSizeConfirmation(collection: string) {
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: 'campaign.widget.component.image.confirm-size-img.text',
        proceedButtonText: 'campaign.widget.component.image.confirm-size-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-size-img.btn-cancel',
      },
    });

    confirmRef.afterClosed().subscribe((result) => {
      if (result === undefined) {
        return;
      }
      const processingType = result ? MediaProcessingType.Resized : MediaProcessingType.MaxSized;
      this.startUpload(collection, processingType);
    });
  }

  private startUpload(collection: string, processingType: MediaProcessingType) {
    this.isUploading = true;
    this.dialogRef.disableClose = true;

    const formData = new FormData();
    formData.set('file', this.data.file, this.data.fileName);
    formData.append('mediaProcessingType', processingType);

    const isVideo = this.data.isVideo;
    const upload$ = isVideo
      ? this.widgetService.uploadVideo(formData, collection)
      : this.widgetService.uploadImage(formData, this.category, collection);

    forkJoin([upload$, timer(500)]).subscribe({
      next: ([uploadResult]) => {
        this.dialogRef.close({
          success: true,
          category: this.category,
          collection: collection,
          uploadResult: uploadResult,
        } as MediaUploadSettings);
      },
      error: (error) => {
        this.dialogRef.close({
          success: false,
          category: this.category,
          collection: collection,
          error: error,
        } as MediaUploadSettings);
      },
    });
  }

  public onCategoryChange(e) {
    const rawCollections = this.data.collections[this.category];
    this.collections = rawCollections.filter((x) => !x.isTriggerbee).map((item) => item.name.replace(/_/g, ' '));
    if (this.data.currentSelection.collection) {
      this.collection = this.data.currentSelection.collection;
      this.collectionControl.setValue(this.collection);
    } else {
      this.collectionControl.setValue('');
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return (this.collections || []).filter((option) => option.toLowerCase().includes(filterValue));
  }

  public trackByCollection(index: number, item: string): string {
    return item;
  }
}
